import { BADGE_NAMES } from 'bundles/product-card/utils/getProductCardStatusTags';
import type { SearchProductHit } from 'bundles/search-common/providers/searchTypes';

/**
 * Normalizes a language value by trimming whitespace and converting to lowercase
 * for consistent comparison.
 */
export const normalize = (s?: string): string => (s ?? '').trim().toLowerCase();

/**
 * Determines which language mode clarity badges to add based on the hit's language properties
 * and the currently selected language filters.
 */
export const getLanguageModeClarityBadges = (
  hit: SearchProductHit,
  shouldShowLanguageModeClarityBadges: boolean,
  selectedLanguageFilters: string[]
): string[] => {
  const badges: string[] = [];

  if (!shouldShowLanguageModeClarityBadges) {
    return badges;
  }
  // Extract and normalize language values from filters, filtering out empty strings and English
  const normalizedFilterLanguages = selectedLanguageFilters
    ?.map((filter) => normalize(filter.replace('language:', '')))
    .filter((lang) => lang.length > 0 && lang !== 'english');
  if (!normalizedFilterLanguages || normalizedFilterLanguages.length === 0) {
    return badges;
  }

  // Normalize hit language arrays for comparison, filtering out empty strings
  const normalizedFullyTranslated =
    hit.fullyTranslatedLanguages?.map(normalize).filter((lang) => lang.length > 0) || [];

  const normalizedSubtitlesOnly = hit.subtitlesOnlyLanguages?.map(normalize).filter((lang) => lang.length > 0) || [];

  const isFullyTranslated = normalizedFilterLanguages.some((filterLang) =>
    normalizedFullyTranslated.includes(filterLang)
  );

  const isSubtitleOnlyTranslated = normalizedFilterLanguages.some((filterLang) =>
    normalizedSubtitlesOnly.includes(filterLang)
  );

  // Priority: Fully Translated takes precedence over Subtitles
  if (isFullyTranslated) {
    badges.push(BADGE_NAMES.FULLY_TRANSLATED);
  } else if (isSubtitleOnlyTranslated) {
    badges.push(BADGE_NAMES.SUBTITLES);
  }

  return badges;
};
